import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { http } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, Upload, CheckCircle2, ShieldCheck, X } from 'lucide-react';

function Chips({ label, options, values, onToggle, testid, allowCustom }) {
  const [draft, setDraft] = useState('');
  const all = [...options, ...values.filter((v) => !options.includes(v))];
  const add = () => {
    const v = draft.trim();
    if (v && !values.includes(v)) onToggle(v);
    setDraft('');
  };
  return (
    <div data-testid={testid}>
      <label className="text-xs uppercase tracking-widest text-navy/50 font-semibold">{label}</label>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {all.map((o) => {
          const on = values.includes(o);
          return (
            <button type="button" key={o} onClick={() => onToggle(o)} data-testid={`${testid}-${o}`}
              className={`text-xs rounded-full px-3 py-1.5 transition-smooth ${on ? 'bg-navy text-white' : 'bg-white text-navy/70 border border-navy/10 hover:bg-beige'}`}>
              {o}
            </button>
          );
        })}
      </div>
      {allowCustom && (
        <div className="mt-2 flex items-center gap-2">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
            placeholder="Add your own…"
            data-testid={`${testid}-custom-input`}
            className="flex-1 bg-white border border-navy/10 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-gold"
          />
          <button type="button" onClick={add} disabled={!draft.trim()} data-testid={`${testid}-custom-add`}
            className="text-xs font-medium bg-beige text-navy rounded-full px-3 py-1.5 hover:brightness-95 disabled:opacity-40">Add</button>
        </div>
      )}
    </div>
  );
}

function Field({ label, v, on, ph, area, type = 'text', testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-navy/50 font-semibold">{label}</label>
      {area ? (
        <textarea value={v} onChange={(e) => on(e.target.value)} rows={4} placeholder={ph} data-testid={testid}
          className="mt-1 w-full bg-white border border-navy/10 rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-gold"/>
      ) : (
        <input type={type} value={v} onChange={(e) => on(e.target.value)} placeholder={ph} data-testid={testid}
          className="mt-1 w-full bg-white border border-navy/10 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold"/>
      )}
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-beige px-4 py-10 lg:py-16" data-testid="therapist-intake-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 justify-center mb-6">
          <span className="w-8 h-8 rounded-lg bg-navy text-gold flex items-center justify-center font-heading text-sm">B+</span>
          <span className="font-heading text-xl text-navy">Brotherhood+</span>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function TherapistIntake({ mode }) {
  const { token } = useParams();
  const manage = mode === 'manage';
  const base = manage ? `/therapist-manage/${token}` : (token ? `/therapist-intake/${token}` : '/therapist-apply');
  const [state, setState] = useState('loading'); // loading | invalid | expired | completed | closed | pending | done
  const [options, setOptions] = useState({ specialties: [], insurance: [], languages: [], states: [] });
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editPending, setEditPending] = useState(false);
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    name: '', credentials: '', bio: '', modality: 'telehealth', photo: '',
    specialties: [], states: [], insurance: [], languages: ['English'],
    booking_url: '', email: '', phone: '', faith_informed: false, trauma_informed: false,
  });

  const set = (k, val) => setForm((f) => ({ ...f, [k]: val }));
  const toggle = (k, val) => setForm((f) => ({ ...f, [k]: f[k].includes(val) ? f[k].filter((x) => x !== val) : [...f[k], val] }));

  const load = useCallback(async () => {
    try {
      const r = await http.get(base);
      if (!r.data.valid) { setState(r.data.reason === 'expired' ? 'expired' : r.data.reason === 'closed' ? 'closed' : 'invalid'); return; }
      if (r.data.status === 'completed') { setState('completed'); return; }
      setOptions(r.data.options || { specialties: [], insurance: [], languages: [], states: [] });
      setEditPending(!!r.data.edit_pending);
      const p = r.data.profile;
      if (p) {
        setForm((f) => ({
          ...f,
          name: p.name || '', credentials: p.credentials || '', bio: p.bio || '',
          modality: (p.modality || 'telehealth'), photo: p.photo || '',
          specialties: p.specialties || [], states: p.states || (p.state ? [p.state] : []),
          insurance: p.insurance || [], languages: p.languages || ['English'],
          booking_url: p.booking_url || p.website || '', email: p.email || '', phone: p.phone || '',
          faith_informed: !!p.faith_informed, trauma_informed: !!p.trauma_informed,
        }));
      } else {
        setForm((f) => ({ ...f, name: r.data.name || '', email: r.data.email || '' }));
      }
      setState('pending');
    } catch { setState('invalid'); }
  }, [base]);

  useEffect(() => { load(); }, [load]);

  const pickPhoto = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) { toast.error('Use a JPG, PNG, or WEBP image.'); return; }
    if (file.size > 5_000_000) { toast.error('Image must be under 5MB.'); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await http.post(`${base}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      set('photo', r.data.url);
      toast.success('Headshot uploaded.');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed.'); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Please enter your full name.'); return; }
    setSubmitting(true);
    try {
      await http.post(base, { ...form, origin: window.location.origin });
      setState('done');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit. Please try again.');
    } finally { setSubmitting(false); }
  };

  if (state === 'loading') return <Shell><div className="text-center py-16 text-navy/45"><Loader2 className="inline animate-spin" size={22}/></div></Shell>;

  if (['invalid', 'expired', 'completed', 'closed'].includes(state)) {
    const msg = {
      invalid: { t: 'This link isn’t valid', s: 'This invitation may have been revoked. Please ask your Brotherhood+ contact for a new link.' },
      expired: { t: 'This link has expired', s: 'Please ask your Brotherhood+ contact to send you a fresh invitation.' },
      completed: { t: 'You’re all set', s: 'This invitation has already been used. Our team will review your profile and reach out if we need anything.' },
      closed: { t: 'Applications are paused', s: 'We’re not accepting new provider applications right now. Please check back soon.' },
    }[state];
    return (
      <Shell>
        <div className="bg-white rounded-3xl p-8 lg:p-10 text-center card-soft" data-testid="intake-status">
          <div className="w-14 h-14 rounded-full bg-beige mx-auto flex items-center justify-center mb-4">
            {state === 'completed' ? <CheckCircle2 className="text-green" size={26}/> : <X className="text-navy/40" size={26}/>}
          </div>
          <h1 className="font-heading text-2xl">{msg.t}</h1>
          <p className="text-navy/65 mt-2">{msg.s}</p>
        </div>
      </Shell>
    );
  }

  if (state === 'done') {
    return (
      <Shell>
        <div className="bg-white rounded-3xl p-8 lg:p-10 text-center card-soft" data-testid="intake-done">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-beige to-gold/45 mx-auto flex items-center justify-center mb-4"><CheckCircle2 className="text-navy" size={30}/></div>
          <h1 className="font-heading text-3xl">{manage ? 'Changes submitted.' : 'Thank you.'}</h1>
          <p className="text-navy/70 mt-3 max-w-md mx-auto">{manage
            ? 'Your changes have been submitted for review. Our team approves every update before it goes live on your listing — we’ll take it from here.'
            : 'Your profile has been submitted for review. Our team vets every provider before they appear in the directory. Once approved, you’ll receive an email with your 7-day free Brotherhood+ trial.'}</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="bg-navy text-white rounded-3xl p-8 lg:p-10 card-soft mb-5">
        <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">{manage ? 'Update your profile' : 'Join the directory'}</p>
        <h1 className="font-heading text-3xl lg:text-4xl mt-2 leading-tight">{manage ? 'Keep your profile current.' : 'Tell men who you are.'}</h1>
        <p className="text-white/75 mt-3">{manage
          ? 'Edit any details below and submit — your changes are reviewed and approved by our team before they go live on your listing.'
          : 'You’ve been invited to be listed in the Brotherhood+ therapist directory — culturally responsive care for men of every background. Share your details below; our team reviews every profile before it goes live.'}</p>
      </div>

      {manage && editPending && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 mb-5 flex items-start gap-2 text-sm text-amber-800" data-testid="intake-edit-pending">
          <ShieldCheck size={16} className="text-amber-600 shrink-0 mt-0.5"/>
          You have an edit awaiting approval. Submitting again will replace it — your live listing stays unchanged until our team approves.
        </div>
      )}

      <div className="bg-white rounded-3xl p-6 lg:p-8 card-soft space-y-5" data-testid="intake-form">
        {/* Headshot */}
        <div>
          <label className="text-xs uppercase tracking-widest text-navy/50 font-semibold">Headshot</label>
          <p className="text-[11px] text-navy/45 mt-0.5">A warm, real photo helps men feel at ease. Square images look best.</p>
          <div className="mt-2 flex items-center gap-4">
            {form.photo
              ? <img src={form.photo} alt="Your headshot" className="w-20 h-20 rounded-full object-cover ring-2 ring-gold/40"/>
              : <div className="w-20 h-20 rounded-full bg-gradient-to-br from-beige to-gold/45 flex items-center justify-center text-navy/40">{uploading ? <Loader2 size={20} className="animate-spin"/> : <Upload size={20}/>}</div>}
            <div>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={pickPhoto} className="hidden"/>
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="intake-photo-upload"
                className="bg-navy text-white rounded-full px-4 py-2 text-xs font-medium inline-flex items-center gap-2 disabled:opacity-50">
                {uploading ? <Loader2 size={13} className="animate-spin"/> : <Upload size={13}/>} {form.photo ? 'Replace photo' : 'Upload headshot'}
              </button>
              {form.photo && <button type="button" onClick={() => set('photo', '')} className="ml-2 text-xs text-red-500">Remove</button>}
            </div>
          </div>
        </div>

        <Field label="Full name *" v={form.name} on={(v) => set('name', v)} ph="Dr. Marcus Bell" testid="intake-name"/>
        <Field label="Credentials" v={form.credentials} on={(v) => set('credentials', v)} ph="LCSW, PhD" testid="intake-credentials"/>
        <Field label="About you" v={form.bio} on={(v) => set('bio', v)} area ph="A few warm sentences about your approach and who you love working with." testid="intake-bio"/>

        <div>
          <label className="text-xs uppercase tracking-widest text-navy/50 font-semibold">How you see clients</label>
          <select value={form.modality} onChange={(e) => set('modality', e.target.value)} data-testid="intake-modality"
            className="mt-1 w-full bg-white border border-navy/10 rounded-xl px-3 py-2 text-sm">
            <option value="telehealth">Telehealth</option>
            <option value="in-person">In-person</option>
            <option value="both">Both</option>
          </select>
        </div>

        <Chips label="States you’re licensed in" options={options.states} values={form.states} onToggle={(v) => toggle('states', v)} testid="intake-states"/>
        <Chips label="Specialties" options={options.specialties} values={form.specialties} onToggle={(v) => toggle('specialties', v)} testid="intake-specialties" allowCustom/>
        <Chips label="Insurance accepted" options={options.insurance} values={form.insurance} onToggle={(v) => toggle('insurance', v)} testid="intake-insurance" allowCustom/>
        <Chips label="Languages" options={options.languages} values={form.languages} onToggle={(v) => toggle('languages', v)} testid="intake-languages" allowCustom/>

        <Field label="Booking link / website" v={form.booking_url} on={(v) => set('booking_url', v)} ph="https://…" testid="intake-booking"/>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Email" v={form.email} on={(v) => set('email', v)} ph="you@practice.com" type="email" testid="intake-email"/>
          <Field label="Phone" v={form.phone} on={(v) => set('phone', v)} ph="(555) 123-4567" testid="intake-phone"/>
        </div>

        <div className="flex flex-wrap gap-4 pt-1">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.faith_informed} onChange={(e) => set('faith_informed', e.target.checked)} className="accent-navy"/> Faith-informed</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.trauma_informed} onChange={(e) => set('trauma_informed', e.target.checked)} className="accent-navy"/> Trauma-informed</label>
        </div>

        <div className="flex items-center gap-2 text-xs text-navy/50 bg-beige rounded-xl px-3 py-2">
          <ShieldCheck size={14} className="text-navy/50 shrink-0"/> {manage
            ? 'Your changes are reviewed and approved by our team before they update your public listing.'
            : 'Your profile is reviewed by our team before it appears publicly.'}
        </div>

        <button onClick={submit} disabled={submitting || uploading} data-testid="intake-submit"
          className="w-full bg-navy text-white rounded-full px-6 py-3.5 font-medium hover:bg-navy-soft disabled:opacity-50 inline-flex items-center justify-center gap-2">
          {submitting && <Loader2 size={16} className="animate-spin"/>} {manage ? 'Submit changes for review' : 'Submit for review'}
        </button>
      </div>

      <p className="text-xs text-navy/45 text-center mt-4">Brotherhood+ lists independent providers and is not a referral service or medical provider.</p>
    </Shell>
  );
}
